const TOKEN_KEY = 'builder_token'; // keep same key — 10+ services read this directly
const REFRESH_KEY = 'builder_refresh_token';
const RETURN_TO_KEY = 'studio:returnTo';

let cachedPayload = null;
let refreshTimer = null;

// ─── Token Storage ─────────────────────────────────────────────────

export const getAccessToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

export const setTokens = (accessToken, refreshToken) => {
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  cachedPayload = null;
  scheduleRefresh();
};

export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem('builder_user_id');
  localStorage.removeItem('builder_permissions');
  cachedPayload = null;
  if (refreshTimer) clearTimeout(refreshTimer);
};

// ─── Return-To After Re-Login ──────────────────────────────────────
// Capture the current path BEFORE an involuntary logout/redirect so that
// after the user logs back in we can restore them to where they were.
// Manual logout MUST NOT call captureReturnTo — that's why we don't bake
// this into clearTokens(). Storage is per-tab (sessionStorage), so a
// user logging out in tab A doesn't leak tab B's return path to tab A.

export const captureReturnTo = () => {
  try {
    const { pathname, search, hash } = window.location;
    // Don't store /login (avoids redirect loop) or the bare root (redundant fallback).
    if (pathname.startsWith('/login') || pathname === '/') return;
    sessionStorage.setItem(RETURN_TO_KEY, pathname + search + hash);
  } catch { /* sessionStorage unavailable; ignore */ }
};

export const consumeReturnTo = () => {
  try {
    const v = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    return v || null;
  } catch { return null; }
};

export const clearReturnTo = () => {
  try { sessionStorage.removeItem(RETURN_TO_KEY); } catch { /* ignore */ }
};

// ─── JWT Decode ────────────────────────────────────────────────────

export const getTokenPayload = () => {
  if (cachedPayload) return cachedPayload;

  try {
    const token = getAccessToken();
    if (!token) return null;
    const payload = token.split('.')[1];
    cachedPayload = JSON.parse(atob(payload));
    return cachedPayload;
  } catch { return null; }
};
// ─── Permission Helpers ────────────────────────────────────────────

export const hasPermission = (module, action = 'read') => {
  const payload = getTokenPayload();
  if (!payload?.permissions) return false;
  return payload.permissions[`${module}_${action}`] === true;
};

export const hasOrgAccess = (orgId) => {
  const payload = getTokenPayload();
  if (!payload?.scope) return false;
  if (payload.scope.full_access) return true;
  if (payload.scope.org_ids === null || payload.scope.org_ids === undefined) return true; // null = unrestricted
  return payload.scope.org_ids.includes(Number(orgId));
};

export const hasAppAccess = (appId) => {
  const payload = getTokenPayload();
  if (!payload?.scope) return false;
  if (payload.scope.full_access) return true;
  if (!payload.scope.app_ids) return true;
  return payload.scope.app_ids.includes(Number(appId));
};

export const isFullAccess = () => getTokenPayload()?.scope?.full_access === true;
export const currentRole = () => getTokenPayload()?.role || '';
export const currentRoleLevel = () => getTokenPayload()?.role_level || 0;
export const isAdmin = () => currentRoleLevel() >= 50;
export const isSuperAdmin = () => currentRoleLevel() >= 100;
export const canManageUsers = () => getTokenPayload()?.can_manage_users === true;

// ─── Silent Refresh ────────────────────────────────────────────────

export const isTokenExpired = () => {
  const payload = getTokenPayload();
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000;
};

const scheduleRefresh = () => {
  if (refreshTimer) clearTimeout(refreshTimer);
  const payload = getTokenPayload();
  if (!payload?.exp) return;

  const expiresAt = payload.exp * 1000;
  const refreshAt = expiresAt - 60000;
  const delay = refreshAt - Date.now();

  if (delay > 0) {
    refreshTimer = setTimeout(() => {
      window.dispatchEvent(new Event('token-needs-refresh'));
    }, delay);
  } else {
    // Defer to next tick so listeners registered in other modules are ready
    refreshTimer = setTimeout(() => {
      window.dispatchEvent(new Event('token-needs-refresh'));
    }, 0);
  }
};

// Detect laptop wake / tab becoming visible — re-check token
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && getAccessToken()) {
    scheduleRefresh(); // will trigger immediate refresh if expired
  }
});

// Silent proactive refresh — fires 60s before token expiry via the timer in
// scheduleRefresh() above. The listener used to live in api.js, but the
// behaviour belongs with the token lifecycle (idle/refresh/cross-tab logic
// all live here). Uses dynamic import inside the handler to avoid an
// auth ↔ authInterceptor module cycle at load time.
window.addEventListener('token-needs-refresh', async () => {
  try {
    const { getIsRefreshing, doRefresh } = await import('./authInterceptor');
    if (getIsRefreshing()) return;
    await doRefresh();
  } catch {
    // Proactive refresh failed — don't logout, let the 401 interceptor handle it
  }
});

// ─── Cross-Tab Token Sync ──────────────────────────────────────────
// `storage` events fire in every OTHER tab (not the writer) whenever
// localStorage changes. We use this single channel for two roles:
//   1. logout fanout — when clearTokens() removes the token, all peer tabs
//      see TOKEN_KEY removed and redirect themselves to /login.
//   2. refresh fanout — when setTokens() writes a fresh access token, peer
//      tabs see the value change and re-schedule their own refresh timers,
//      so they don't make a redundant /auth/refresh call of their own.
window.addEventListener('storage', (e) => {
  if (e.key !== TOKEN_KEY) return;
  cachedPayload = null;
  if (e.newValue === null) {
    // Another tab cleared the token. Match its state but DON'T call
    // clearTokens() again — that would touch localStorage and loop the event.
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    // Capture this tab's current path BEFORE redirecting so re-login lands
    // back here, not on the peer tab's page.
    captureReturnTo();
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
  } else if (e.newValue !== e.oldValue) {
    // Another tab refreshed the access token — adopt the new exp.
    scheduleRefresh();
  }
});

if (getAccessToken()) {
  scheduleRefresh();
}
